import { makeAutoObservable, runInAction } from 'mobx';
import type balanceAgent from '../../services/api/balanceAgent';
import type { TQuotaBalance } from '../../services/models/QuotaModels';

export interface IBalanceStoreDependencies {
  agent: typeof balanceAgent;
}

/**
 * Whose credits are on screen: the person signed in, and the owner a superAdmin works as, by id, or
 * null while they work as themselves. Never a display name: two owners may share one, or have none.
 */
export interface IBalanceOwner {
  signedInUserId: string;
  impersonatedOwnerId: number | null;
}

const isSameOwner = (a: IBalanceOwner | null, b: IBalanceOwner | null): boolean => (a === null || b === null ? a === b : a.signedInUserId === b.signedInUserId && a.impersonatedOwnerId === b.impersonatedOwnerId);

/**
 * The author's credits, as the navigation bar shows them.
 *
 * A balance arrives from more than one place: this store's own read of the balance endpoint, and —
 * pushed in through `accept` by later work — the media generate response, the media terminal events,
 * the course completion and the purchase confirmation. Whatever carried it, the newest by version wins.
 *
 * Imports no agent, no store and no React; the agent is a port, wired in `createBalanceStore`.
 */
export default class BalanceStore {
  /** The balance to show, or null when there is none to trust — and then nothing is shown. */
  balance: TQuotaBalance | null = null;

  private deps: IBalanceStoreDependencies;

  /** Raised by `forget`. A read answers only into the generation it was set off in. */
  private ownerGeneration = 0;

  /** The owner whose credits are on screen, as `showCreditsOf` was last told. */
  private owner: IBalanceOwner | null = null;

  /**
   * True from `forget` until a read set off after it has answered or failed. Meanwhile a pushed balance
   * cannot be told from one for the previous owner still on its way, so none is taken.
   */
  private isAwaitingOwnersRead = false;

  /**
   * The account the last read since `forget` answered for — the paying owner's, as the backend decides
   * it — or null when no such read has answered. A pushed balance for another account is dropped.
   */
  private readOwnerId: number | null = null;

  constructor(dependencies: IBalanceStoreDependencies) {
    this.deps = dependencies;

    makeAutoObservable<this, 'deps' | 'ownerGeneration' | 'owner' | 'isAwaitingOwnersRead' | 'readOwnerId'>(this, {
      deps: false,
      ownerGeneration: false,
      owner: false,
      isAwaitingOwnersRead: false,
      readOwnerId: false,
    });
  }

  /** Monthly and Purchased together — Purchased counted as it is, below zero included. Null with no balance. */
  get totalCredits(): number | null {
    return this.balance === null ? null : this.balance.monthly + this.balance.purchased;
  }

  /**
   * The one way a balance gets in, from whatever carried it. Kept only when it is newer than the one
   * held — by the account's version, never by when it arrived: balances come from two hosts with two
   * clocks, and an answer that set off first can land last. A balance for another account than the one
   * the owner's read answered for is a previous owner's, still on its way, and is dropped.
   */
  accept = (balance: TQuotaBalance): void => {
    if (this.isAwaitingOwnersRead) return;
    if (this.readOwnerId !== null && balance.ownerId !== this.readOwnerId) return;
    this.keepIfNewer(balance);
  };

  private keepIfNewer = (balance: TQuotaBalance): void => {
    if (this.balance !== null && balance.version <= this.balance.version) return;
    this.balance = balance;
  };

  /**
   * Shows the credits of `owner`, or nothing while nobody is signed in. A different owner than the one
   * on screen is a switch: the balance is forgotten and read afresh. The same owner again — a token
   * refresh hands back the same person — reads nothing.
   */
  showCreditsOf = (owner: IBalanceOwner | null): void => {
    if (isSameOwner(owner, this.owner)) return;
    this.owner = owner;
    this.forget();
    if (owner !== null) void this.load();
  };

  /**
   * Reads the balance from the backend. When the read fails, nothing is shown rather than a number
   * that may be wrong — unless a balance arrived from elsewhere while the read was on its way, which
   * is as current as anything the read could have said.
   */
  load = async (): Promise<void> => {
    const ownerAtStart = this.ownerGeneration;
    const heldBefore = this.balance;
    try {
      const answered = await this.deps.agent.Quota.getBalance();
      runInAction(() => {
        if (this.ownerGeneration !== ownerAtStart) return;
        this.isAwaitingOwnersRead = false;
        this.readOwnerId = answered.ownerId;
        this.keepIfNewer(answered);
      });
    } catch {
      runInAction(() => {
        if (this.ownerGeneration !== ownerAtStart) return;
        this.isAwaitingOwnersRead = false;
        if (this.balance === heldBefore) this.balance = null;
      });
    }
  };

  /**
   * Lets go of the balance held, because whose credits these are has changed: a superAdmin started
   * or stopped working as another owner, or someone else signed in. Two owners' versions cannot be
   * compared, so the next balance is taken whatever its version, and a read set off for the previous
   * owner is dropped when it answers. A balance pushed in before the next owner's own read has
   * answered may be the previous owner's, still on its way, so it is dropped too; after it has
   * answered, the owner id that read carried tells the previous owner's apart.
   */
  forget = (): void => {
    this.ownerGeneration += 1;
    this.isAwaitingOwnersRead = true;
    this.readOwnerId = null;
    this.balance = null;
  };
}
